import { motion } from "framer-motion";
import { Quote, Sparkles, AlertTriangle, Info, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getOutboundProps } from "../config/affiliateLinks";
import { getToolByAffiliateKey } from "../data/tools";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const CalloutIcon = { info: Info, warning: AlertTriangle, tip: Sparkles };

export const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60);

export const ArticleRenderer = ({ blocks = [] }) => {
  return (
    <div className="space-y-8">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "h2":
            return (
              <motion.h2 {...fadeUp} id={slugify(block.text)} key={i} className="font-display font-bold text-2xl sm:text-3xl text-white tracking-tight pt-6 border-t border-white/10 first:border-t-0 first:pt-0 scroll-mt-24">
                {block.text}
              </motion.h2>
            );
          case "h3":
            return (
              <motion.h3 {...fadeUp} key={i} className="font-display font-bold text-xl text-white tracking-tight">
                {block.text}
              </motion.h3>
            );
          case "p":
            return (
              <motion.p {...fadeUp} key={i} className="text-base leading-relaxed text-zinc-300 max-w-[70ch]">
                {block.text}
              </motion.p>
            );
          case "list":
            return (
              <motion.ul {...fadeUp} key={i} className="space-y-3">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-3 text-zinc-300 text-base leading-relaxed max-w-[68ch]">
                    <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </motion.ul>
            );
          case "numbered":
            return (
              <motion.ol {...fadeUp} key={i} className="space-y-4">
                {block.items.map((item, j) => (
                  <li key={j} className="flex gap-4 text-zinc-300 text-base leading-relaxed max-w-[68ch]">
                    <span className="font-mono-tag text-gold text-sm shrink-0 mt-0.5">{String(j + 1).padStart(2, "0")}</span>
                    {item}
                  </li>
                ))}
              </motion.ol>
            );
          case "quote":
            return (
              <motion.blockquote {...fadeUp} key={i} className="border-l-2 border-gold pl-6 py-2 text-lg text-silver italic leading-relaxed max-w-[65ch]">
                <Quote className="w-5 h-5 text-gold mb-2 -ml-1" aria-hidden="true" />
                {block.text}
              </motion.blockquote>
            );
          case "callout": {
            const Icon = CalloutIcon[block.variant] || Info;
            return (
              <motion.div {...fadeUp} key={i} className="bg-surface border border-white/10 rounded-sm p-5 flex gap-3 items-start">
                <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm text-zinc-400 leading-relaxed">{block.text}</p>
              </motion.div>
            );
          }
          case "stat-row":
            return (
              <motion.div {...fadeUp} key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-2">
                {block.stats.map((s, j) => (
                  <div key={j} className="border border-white/10 rounded-sm p-4 bg-surface/50">
                    <div className="font-mono-tag text-2xl font-medium text-gradient-gold">{s.value}</div>
                    <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wide">{s.label}</div>
                  </div>
                ))}
              </motion.div>
            );
          case "before-after":
            return (
              <motion.div {...fadeUp} key={i} className="space-y-3" data-testid="before-after-block">
                {block.label && <p className="text-xs font-mono-tag uppercase tracking-[0.15em] text-zinc-500">{block.label}</p>}
                <div className="grid sm:grid-cols-2 gap-px bg-white/10 rounded-sm overflow-hidden">
                  <div className="bg-surface p-5">
                    <span className="text-xs font-mono-tag text-zinc-500 uppercase tracking-widest">Before</span>
                    <p className="text-zinc-500 text-sm leading-relaxed mt-3 whitespace-pre-line">{block.before}</p>
                  </div>
                  <div className="bg-surface p-5 relative ring-1 ring-gold/20">
                    <span className="text-xs font-mono-tag text-gold uppercase tracking-widest">After</span>
                    <p className="text-silver text-sm leading-relaxed mt-3 whitespace-pre-line">{block.after}</p>
                  </div>
                </div>
              </motion.div>
            );
          case "table":
            return (
              <motion.div {...fadeUp} key={i} className="overflow-x-auto scrollbar-hide rounded-sm border border-white/10">
                <table className="matrix-table w-full text-sm">
                  <thead>
                    <tr className="bg-surface">
                      {block.headers.map((h, j) => (
                        <th key={j} className="text-left px-4 py-3 font-mono-tag text-xs uppercase tracking-wider text-zinc-400 border-b border-white/10 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, j) => (
                      <tr key={j} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                        {row.map((cell, k) => (
                          <td key={k} className="px-4 py-3 text-zinc-300 align-top">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            );
          case "cta": {
            const outbound = block.affiliateKey ? getOutboundProps(block.affiliateKey) : null;
            const isInternal = !block.affiliateKey && block.href?.startsWith("/");
            return (
              <motion.div {...fadeUp} key={i} className="spotlight border border-white/10 rounded-sm p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div>
                  <h3 className="font-display font-bold text-xl text-white">{block.heading}</h3>
                  <p className="text-zinc-400 text-sm mt-1 max-w-md">{block.text}</p>
                </div>
                {isInternal ? (
                  <Link to={block.href} data-testid="article-cta-link" className="shrink-0 h-11 px-6 bg-primary hover:bg-[#5C7CFA] text-white font-medium text-sm rounded-sm inline-flex items-center justify-center transition-colors">
                    {block.buttonText}
                  </Link>
                ) : outbound ? (
                  <a href={outbound.href} target={outbound.target} rel={outbound.rel} data-testid="article-cta-link" className="shrink-0 h-11 px-6 bg-primary hover:bg-[#5C7CFA] text-white font-medium text-sm rounded-sm inline-flex items-center justify-center transition-colors">
                    {block.buttonText}
                  </a>
                ) : null}
              </motion.div>
            );
          }
          case "bridge-cta": {
            const outbound = getOutboundProps(block.primaryKey);
            const resolvedTool = getToolByAffiliateKey(block.primaryKey);
            return (
              <motion.div {...fadeUp} key={i} className="spotlight border border-gold/25 rounded-sm p-8">
                <h3 className="font-display font-bold text-xl text-white">{block.headline}</h3>
                <p className="text-zinc-400 text-sm mt-2 max-w-lg leading-relaxed">{block.body}</p>
                <div className="flex flex-wrap items-center gap-5 mt-6">
                  {outbound && (
                    <a
                      href={outbound.href}
                      target={outbound.target}
                      rel={outbound.rel}
                      data-testid="article-bridge-cta-link"
                      className="h-11 px-6 bg-primary hover:bg-[#5C7CFA] text-white font-medium text-sm rounded-sm inline-flex items-center justify-center gap-2 transition-colors"
                    >
                      {block.primaryLabel || `See ${resolvedTool?.name || ""}`} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </a>
                  )}
                  <Link to={block.secondaryHref} className="text-sm text-zinc-400 hover:text-white underline underline-offset-4">
                    {block.secondaryLabel}
                  </Link>
                </div>
              </motion.div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
};
